import React, { useRef, useState } from 'react'
import { BackButton } from '../../../Common/CtaButton/BackButton'
import { EntryButton } from '../../../Common/CtaButton/EntryButton'
import { ImageCheckbox } from '../../../Common/EntryForm/ImageCheckbox'
import { useStepContext } from '@/components/Context/StepContext'
import styles from './Tell.module.css'
import { TellProps } from './types'
import { useAbTestGtm } from '@/components/utils/abTest'
import { useStableFlag } from '@/components/utils/hooks/useStableFlag'
import { FollowIcon } from '../../../FormGuidance/FollowIcon'
import { useFollowIcon } from '../../../FormGuidance/FollowIcon/useFollowIcon'

export const Tell = ({ gtmTags, isSubmitting }: TellProps) => {
    const {
        updateFormData,
        formData,
        errors,
        touchField,
        touchedFields,
        isAnimating,
        currentStep,
    } = useStepContext()

    const parentRef = useRef<HTMLDivElement>(null)
    const emailFieldRef = useRef<HTMLDivElement>(null)
    const phoneFieldRef = useRef<HTMLDivElement>(null)
    const submitButtonRef = useRef<HTMLDivElement>(null)
    const [isEmailFocused, setIsEmailFocused] = useState(false)

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFormData('email', e.target.value)
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFormData('phone', e.target.value)
    }

    const handleLineIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()

        updateFormData('lineId', value)
        if (value !== '' && !formData.preferLineContact) {
            updateFormData('preferLineContact', true)
        }
    }

    const handleBlur = (field: 'email' | 'phone') => {
        touchField(field)
    }

    const handleLineCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked

        updateFormData('preferLineContact', isChecked)
        if (!isChecked && formData.lineId) {
            updateFormData('lineId', '')
        }
    }

    const isEmailValid = !!formData.email && !errors.email
    const isPhoneValid = !!formData.phone && !errors.phone
    // メール入力中にバリデーションが有効⇔無効を行き来することによるチラつき防止
    const isEmailStable = useStableFlag(isEmailValid)

    const showEmailError = touchedFields.email && errors.email
    const showPhoneError = touchedFields.phone && errors.phone

    const isFormValid =
        formData.email &&
        formData.phone &&
        formData.agreeTerms &&
        !errors.email &&
        !errors.phone

    const phoneTarget = {
        ref: phoneFieldRef,
        isComplete: isEmailFocused && isPhoneValid,
        offset: 46,
    }
    const emailTarget = {
        ref: emailFieldRef,
        isComplete: isEmailStable,
        offset: 46,
    }
    const targets = [
        phoneTarget,
        emailTarget,
        {
            ref: submitButtonRef,
            isComplete: !!isFormValid,
        },
    ]
    const { iconStyle } = useFollowIcon({ parentRef, targets, isAnimating, layoutKey: currentStep })

    const { dataViewLabel, dataClickLabel } = useAbTestGtm()

    const emailField = (
        <div className={styles.inputField} ref={emailFieldRef}>
            <div className={styles.labelWrapper}>
                <label className={styles.label}>メールアドレス</label>
                <span className={styles.required}>必須</span>
            </div>
            <input
                type='email'
                className={`${styles.textInput} ${showEmailError ? styles.inputError : ''}`}
                value={formData.email}
                onChange={handleEmailChange}
                onFocus={() => setIsEmailFocused(true)}
                onBlur={() => handleBlur('email')}
                placeholder='例：foodslabo@cuolega.jp'
            />
            {showEmailError && (
                <div className={styles.errorMessage}>{errors.email}</div>
            )}
        </div>
    )

    const phoneField = (
        <div className={styles.inputField} ref={phoneFieldRef}>
            <div className={styles.labelWrapper}>
                <label className={styles.label}>電話番号</label>
                <span className={styles.required}>必須</span>
            </div>
            <input
                type='tel'
                className={`${styles.textInput} ${showPhoneError ? styles.inputError : ''}`}
                value={formData.phone}
                onChange={handlePhoneChange}
                onBlur={() => handleBlur('phone')}
                placeholder='例：09012345678'
            />
            {showPhoneError && (
                <div className={styles.errorMessage}>{errors.phone}</div>
            )}
        </div>
    )

    return (
        <div className={styles.wrapper} ref={parentRef} data-view-label={dataViewLabel(gtmTags.dataViewLabel)}>
            <FollowIcon iconStyle={iconStyle} />
            <div className={styles.title}>
                <BackButton dataClickLabel={dataClickLabel(gtmTags.dataClickLabels.back)} />
                <h2
                    className={styles.titleText}
                >
                    Q.ご連絡先は？
                </h2>
            </div>

            <div className={styles.formContainer}>
                {phoneField}
                {emailField}

                <div className={styles.inputField}>
                    <div className={styles.checkbox}>
                        <ImageCheckbox
                            checked={formData.preferLineContact}
                            onChange={handleLineCheckboxChange}
                            name='lineCheckbox'
                            altPurpose='LINEからのやり取りを希望する'
                        />
                        <span className={styles.checkboxText}>
                            LINEからのやり取りを希望する
                        </span>
                    </div>
                    <input
                        type='text'
                        className={styles.textInput}
                        value={formData.lineId}
                        onChange={handleLineIdChange}
                        placeholder='LINEアカウントのID（任意）'
                    />
                </div>
                <div className={styles.annotationParamWrap}>
                    <p>
                        <a href={'https://cuolega.co.jp/terms/?fpc=4901.607.365.b01e006616d8030K.1742824673000'}
                            data-click-label={dataClickLabel(gtmTags.dataClickLabels.link)}>
                            利用規約・個人情報の取り扱い</a>（利用規約第4条、第5条）同意しご登録いただける場合は、下記の「同意して次へ進む」を押してください。
                    </p>
                </div>
            </div>
            <EntryButton
                ref={submitButtonRef}
                disabled={!isFormValid || isSubmitting}
                isSubmitting={isSubmitting}
                dataClickLabel={dataClickLabel(gtmTags.dataClickLabels.cta)}
            />
        </div>
    )
}
